// ============================================================
// Email ingest — shared types (schema-aligned)
// Mirrors berserkr/Email_Ingest_Schema_Deltas_v0.md and the validated
// reference parser berserkr/scripts/email_parser_ref.py.
// ============================================================

export type ExportRoute =
  | "ios_mail_print"
  | "gmail_chrome_print"
  | "eml"
  | "generic";

export type HeaderGrammar =
  | "apple"
  | "outlook"
  | "gmail_wrapper"
  | "ocr_band"
  | "cover_note";

export type SpeakerRole = "plaintiff" | "defendant" | "neutral" | "attorney" | "unknown";

export interface Addr {
  display_name: string | null;
  address: string | null;
}

export interface SenderParty {
  role: SpeakerRole;
  represents: string | null; // for attorneys: whose counsel ("defendant" | "plaintiff" | "estate" | ...)
  party_name: string | null;
  firm: string | null;
}

export interface AttachmentRef {
  filename: string;
  ref_only: boolean; // true = referenced in text, binary not present (PDF route)
  mime_type: string | null;
  size_bytes: number | null;
  content_hash: string | null; // set when binary supplied
  storage_path: string | null; // bucket key when binary stored
}

export type PiiKind = "ssn" | "account" | "dob" | "third_party_phi" | "phone" | "other";

export interface PiiFlag {
  kind: PiiKind;
  span: [number, number]; // char offsets into body
  preview: string; // masked preview for review UI
  resolved: boolean;
  redact: boolean;  // user toggle: mask this span in the committed body
}

export interface EmailMessage {
  body: string;
  sender_name: string | null;
  sender_party: SenderParty | null;
  message_index: number;
  source_kind: "email";
  from_address: Addr | null;
  to_addresses: Addr[];
  cc_addresses: Addr[];
  subject: string | null;
  date: string | null; // ISO; real header timestamp
  date_raw: string | null;
  date_tz: string | null;
  date_kind: "Date" | "Sent" | null;
  date_source: "text" | "ocr" | null;
  in_quoted_history: boolean; // true => this copy is a re-quote
  canonical_message_id: string; // stable; re-quotes share the canonical id
  requote_count: number; // on canonical: number of re-quotes seen
  header_grammar: HeaderGrammar;
  attachments: AttachmentRef[];
  pii_flags: PiiFlag[];
}

export interface ParsedEmailThread {
  source_file: string;
  exhibit_id: string | null;
  export_route: ExportRoute;
  build_stamp: string;
  schema_version: number;
  thread_subject: string | null;
  thread_first_at: string | null;
  thread_last_at: string | null;
  message_count: number; // canonical messages
  total_occurrences: number; // incl. re-quotes
  messages: EmailMessage[]; // store-all (canonical + re-quotes)
  warnings: string[];
}

export const BUILD_STAMP = "email-ingest/0.1";
export const SCHEMA_VERSION = 1;
